import { Checkbox } from '@chakra-ui/react';
import type { StandardTableFeatures } from '@ionq/legacy-ui';
import type { Table as ReactTable, Row, RowData } from '@tanstack/react-table';

/**
 * The select-column cell, shared by MyJobs and the table tests so the suite
 * pins the real wiring. Selection rides getToggleSelectedHandler on the
 * hidden input's CLICK event — checked is already post-toggle and shiftKey
 * rides the same event, so Shift-click selects the display-order range from
 * the anchor and a plain click toggles. Deliberately NOT onChange: React
 * gates change delivery behind its input value tracker, and zag syncs the
 * controlled input through the prototype setter the tracker can't see — so
 * after any programmatic sync (select-all, an incoming Shift range) the
 * next deselect click's change event is swallowed.
 */
export function SelectRowCell<TData extends RowData>({
    row,
}: {
    row: Row<StandardTableFeatures, TData>;
}) {
    return (
        <Checkbox.Root
            size='sm'
            aria-label={`Select job ${row.id}`}
            checked={row.getIsSelected()}
            // Block-level, not the recipe's inline-flex + vertical-align:top
            // — on the cell's 20px line box the 16px control would sit ~2px
            // above the row's vertical center (same fix as the header cell).
            display='flex'
        >
            <Checkbox.HiddenInput onClick={row.getToggleSelectedHandler()} />
            <Checkbox.Control />
        </Checkbox.Root>
    );
}

/**
 * The select-column HEADER checkbox: toggles every row loaded so far
 * (TanStack's all-rows selection covers the row model, i.e. the fetched
 * pages — the action bar's "Select all N rows" is the whole-set upgrade).
 * Argument-less toggleAllRowsSelected instead of the event handler: from
 * the indeterminate state zag pre-sets the input checked, so the click's
 * post-toggle target.checked reads false and would DESELECT — the
 * argument-less form flips on !getIsAllRowsSelected(), giving the native
 * tri-state behavior (indeterminate → select all). Fresh state reaches the
 * header on every selection change because the selection is controlled:
 * the owner re-renders the table, and headers render inline.
 */
export function SelectAllRowsCell<TData extends RowData>({
    table,
}: {
    table: ReactTable<StandardTableFeatures, TData>;
}) {
    return (
        <Checkbox.Root
            size='sm'
            aria-label='Select all loaded jobs'
            checked={
                table.getIsAllRowsSelected()
                    ? true
                    : table.getIsSomeRowsSelected()
                      ? 'indeterminate'
                      : false
            }
            // The shared header row pads 16px vs the body cells' 12px —
            // pull back the difference so the checkbox column aligns.
            ml='-4px'
            // Block-level, not the recipe's inline-flex + vertical-align:top:
            // inside the header label's 21px line box the 16px control would
            // ride the line top, ~3px above the row's vertical center.
            display='flex'
        >
            <Checkbox.HiddenInput
                onClick={() => table.toggleAllRowsSelected()}
            />
            <Checkbox.Control />
        </Checkbox.Root>
    );
}
